import type { RouteLocationNormalized } from 'vue-router'
import type { RightyModule, RightyInvocationContext } from '../types'
import { ROUTES } from '@/router/routeNames'

/**
 * Maps Vue route names to Righty modules.
 * This determines which tools are available based on where the user is.
 */
const ROUTE_TO_MODULE_MAP: Record<string, RightyModule> = {
  // Flow routes
  [ROUTES.OFFERFUNNELS]: 'flow',
  [ROUTES.OFFERFUNNEL]: 'flow',
  [ROUTES.CREATEOFFERFUNNEL]: 'flow',
  [ROUTES.OFFERFUNNELNODE]: 'flow',
  [ROUTES.OFFERFUNNELOFFER]: 'flow',
  [ROUTES.OFFERFUNNELSEGMENTGROUP]: 'flow',
  [ROUTES.OFFERFUNNELWIDGET]: 'flow',

  // Widget routes
  [ROUTES.WIDGETS]: 'widget',
  [ROUTES.WIDGET]: 'widget',
  [ROUTES.CREATEWIDGET]: 'widget',
  [ROUTES.WIDGETOFFERFUNNEL]: 'widget',
  [ROUTES.WIDGETOFFERFUNNELOFFER]: 'widget',
  [ROUTES.WIDGETOFFERFUNNELSEGMENTGROUP]: 'widget',
  [ROUTES.WIDGETOFFER]: 'widget',

  // Segment routes
  [ROUTES.SEGMENTGROUPSPARENT]: 'segment',
  [ROUTES.SEGMENTGROUPS]: 'segment',
  [ROUTES.SEGMENTGROUP]: 'segment',
  [ROUTES.SEGMENTGROUPCREATE]: 'segment',
  [ROUTES.SEGMENTGROUPTEMPLATES]: 'segment',
  [ROUTES.QUESTIONS]: 'segment',
  [ROUTES.QUESTION]: 'segment',
  [ROUTES.QUESTIONCREATE]: 'segment',

  // Campaign/Personalization routes
  [ROUTES.CAMPAIGNS]: 'campaign',
  [ROUTES.CAMPAIGN]: 'campaign',
  [ROUTES.CREATECAMPAIGN]: 'campaign',
  [ROUTES.CREATECAMPLAIGNWITHINDASHBOARD]: 'campaign',

  // Email personalization routes
  [ROUTES.EMAILS]: 'email',
  [ROUTES.EMAIL]: 'email',

  // Offer routes
  [ROUTES.OFFERS]: 'offer',
  [ROUTES.OFFER]: 'offer',
  [ROUTES.FORMS]: 'offer',
  [ROUTES.FORM]: 'offer',

  // Dashboard routes
  [ROUTES.DASHBOARD]: 'dashboard',
  [ROUTES.DASHBOARD_INDEX]: 'dashboard',
  [ROUTES.GOALS]: 'dashboard',
  [ROUTES.GOAL]: 'dashboard',
  [ROUTES.CREATEGOAL]: 'dashboard',

  // Integration routes
  [ROUTES.INTEGRATIONS]: 'integration',
  [ROUTES.INTEGRATION]: 'integration',

  // Insights routes
  [ROUTES.INSIGHTS]: 'insights',
  [ROUTES.ATTRIBUTION]: 'insights',
  [ROUTES.PAGES]: 'insights',
  [ROUTES.METRICS]: 'insights',

  // Settings routes
  [ROUTES.SETTINGS]: 'settings',
  [ROUTES.PROFILE]: 'settings',
  [ROUTES.ACCOUNTS]: 'settings',
  [ROUTES.SECURITY]: 'settings',
  [ROUTES.USERS]: 'settings',
  [ROUTES.USER]: 'settings',

  // Visitors routes
  [ROUTES.PEOPLE]: 'visitors',
  [ROUTES.VISITOR_DETAIL]: 'visitors'
}

/**
 * Route parameters that indicate resource IDs
 */
const RESOURCE_PARAM_MAP: Record<string, { type: RightyInvocationContext['resourceType']; param: string }> = {
  [ROUTES.OFFERFUNNEL]: { type: 'flow', param: 'offerFunnelId' },
  [ROUTES.OFFERFUNNELNODE]: { type: 'flow', param: 'offerFunnelId' },
  [ROUTES.WIDGET]: { type: 'widget', param: 'widgetId' },
  [ROUTES.SEGMENTGROUP]: { type: 'dimension', param: 'segmentGroupId' },
  [ROUTES.QUESTION]: { type: 'question', param: 'questionId' },
  [ROUTES.CAMPAIGN]: { type: 'campaign', param: 'campaignId' },
  [ROUTES.OFFER]: { type: 'offer', param: 'offerId' },
  [ROUTES.FORM]: { type: 'offer', param: 'formId' },
  [ROUTES.VISITOR_DETAIL]: { type: 'visitor', param: 'visitorId' },
  [ROUTES.EMAIL]: { type: 'email', param: 'emailId' }
}

/**
 * Derives the Righty module from the current route.
 */
export function deriveModuleFromRoute(route: RouteLocationNormalized): RightyModule {
  const routeName = route.name as string

  // Check direct mapping first
  if (routeName && ROUTE_TO_MODULE_MAP[routeName]) {
    return ROUTE_TO_MODULE_MAP[routeName]
  }

  // Fall back to path-based detection
  const path = route.path.toLowerCase()

  if (path.includes('/flows')) return 'flow'
  if (path.includes('/widgets')) return 'widget'
  if (path.includes('/segments') || path.includes('/questions')) return 'segment'
  if (path.includes('/campaigns') || path.includes('/personalizer')) return 'campaign'
  if (path.includes('/offers') || path.includes('/forms')) return 'offer'
  if (path.includes('/integrations')) return 'integration'
  if (path.includes('/insights') || path.includes('/attribution')) return 'insights'
  if (path.includes('/settings')) return 'settings'
  if (path.includes('/people')) return 'visitors'

  // Default to dashboard
  return 'dashboard'
}

/**
 * Extracts resource information from the route and stores.
 */
export function extractResourceInfo(
  route: RouteLocationNormalized,
  configStore?: any
): Pick<RightyInvocationContext, 'resourceType' | 'resourceId' | 'resourceName'> {
  const routeName = route.name as string
  const resourceMapping = RESOURCE_PARAM_MAP[routeName]

  if (!resourceMapping) {
    return {}
  }

  const resourceId = route.params[resourceMapping.param] as string
  if (!resourceId) {
    return {}
  }

  let resourceName: string | undefined

  // Try to get resource name from config store if available
  if (configStore) {
    resourceName = getResourceNameFromConfig(configStore, resourceMapping.type, resourceId)
  }

  return {
    resourceType: resourceMapping.type,
    resourceId,
    resourceName
  }
}

/**
 * Gets a resource name from the config store based on type and ID.
 */
function getResourceNameFromConfig(
  configStore: any,
  resourceType: RightyInvocationContext['resourceType'],
  resourceId: string
): string | undefined {
  if (!configStore?.config) return undefined

  const config = configStore.config

  switch (resourceType) {
    case 'flow': {
      const flow = config.rightCta?.offerFunnels?.find((f: any) => f.id === resourceId)
      return flow?.name
    }
    case 'widget': {
      const widget = config.rightCta?.widgets?.find((w: any) => w.id === resourceId)
      return widget?.name
    }
    case 'dimension': {
      const dimension = config.dimensions?.find((d: any) => d.id === resourceId)
      return dimension?.name
    }
    case 'question': {
      const question = config.rightAsk?.questions?.find((q: any) => q.id === resourceId)
      return question?.text?.substring(0, 50)
    }
    case 'campaign': {
      const campaign = config.campaigns?.find((c: any) => c.id === resourceId)
      return campaign?.name
    }
    case 'offer': {
      const offer = config.rightCta?.offers?.find((o: any) => o.id === resourceId)
      return offer?.name
    }
    default:
      return undefined
  }
}

/**
 * Extracts additional context from the route (e.g., editing node ID).
 */
export function extractAdditionalContext(
  route: RouteLocationNormalized
): RightyInvocationContext['additionalContext'] | undefined {
  const routeName = route.name as string
  const additionalContext: RightyInvocationContext['additionalContext'] = {}

  // For flow node editing, capture the UUID
  if (routeName === ROUTES.OFFERFUNNELNODE && route.params.uuid) {
    additionalContext.editingNodeId = route.params.uuid as string
  }

  // For widget editing, capture the widget type from route meta or query
  if (routeName === ROUTES.WIDGET && route.query.type) {
    additionalContext.widgetType = route.query.type as string
  }

  // Check if viewing a question specifically
  if (routeName === ROUTES.QUESTION) {
    additionalContext.isQuestion = true
  }

  return Object.keys(additionalContext).length > 0 ? additionalContext : undefined
}

/**
 * Routes that support undo/redo and should force auto-apply on all Righty plans.
 * Since users can easily undo changes, confirmation is unnecessary friction.
 */
const ROUTES_WITH_UNDO_REDO: string[] = [
  ROUTES.OFFERFUNNEL,
  ROUTES.OFFERFUNNELNODE,
  ROUTES.EMAIL
]

/**
 * Builds the complete Righty invocation context from the current route.
 */
export function buildContextFromRoute(
  route: RouteLocationNormalized,
  configStore?: any
): RightyInvocationContext {
  const module = deriveModuleFromRoute(route)
  const resourceInfo = extractResourceInfo(route, configStore)
  const additionalContext = extractAdditionalContext(route)
  const forceAutoApply = ROUTES_WITH_UNDO_REDO.includes(route.name as string)

  return {
    module,
    route: route.path,
    routeName: route.name as string | undefined,
    ...resourceInfo,
    ...(additionalContext ? { additionalContext } : {}),
    ...(forceAutoApply ? { forceAutoApply } : {})
  }
}

/**
 * Checks if two contexts represent the same "page" for thread clearing purposes.
 * Returns true if the thread should be preserved (same page/resource).
 */
export function isSameContext(
  oldContext: RightyInvocationContext | null,
  newContext: RightyInvocationContext
): boolean {
  if (!oldContext) return false

  // Different module = definitely different context
  if (oldContext.module !== newContext.module) {
    return false
  }

  // Same module but different resource = different context
  if (oldContext.resourceId !== newContext.resourceId) {
    return false
  }

  // Same module and same resource (or both no resource) = same context
  return true
}
